import { FC } from 'react';

import { BoxProps, Typography, TypographyProps } from '@mui/material';
import { useField } from 'formik';

import { Flex } from 'src/components/Flex';

interface RateInputField {
    name: string;
    label?: string;
    disabled?: boolean;
    flexDirection?: 'row' | 'column';
    textStyle?: TypographyProps;
    containerStyle?: BoxProps;
}

export const RateInputField: FC<RateInputField> = ({
    name,
    label,
    disabled,
    flexDirection,
    textStyle,
    containerStyle,
}) => {
    const [{ value }, { error, touched }, { setValue }] = useField(name);

    const hasError = disabled ? false : error && touched;

    const borderColor = hasError ? 'red' : '#F3A256';

    const isNameLocality = name.includes('locality');

    const localityValues = {
        interRegional: {
            purchases: {
                percentage: 1.25,
                costPerItem: 6.5,
            },
            refunds: {
                percentage: 1.25,
                costPerItem: 6.5,
            },
        },
        intraRegional: {
            purchases: {
                percentage: 1,
            },
            refunds: {
                percentage: 1,
            },
        },
    };

    let currentValue = value;

    if (isNameLocality) {
        const localityKey = name.split('.')[2];

        const localityData = localityValues[localityKey];

        if (name.endsWith('.percentage')) {
            currentValue = localityData.purchases.percentage;
        } else if (name.endsWith('.costPerItem')) {
            currentValue = localityData.purchases.costPerItem;
        }
    }

    return (
        <Flex
            justifyContent="space-between"
            flexDirection={flexDirection}
            alignItems="center"
            gap="16px"
            {...containerStyle}
        >
            {label && (
                <Typography
                    fontSize="16px"
                    fontWeight={400}
                    color="#575757"
                    lineHeight="24px"
                    sx={{ maxLines: 1 }}
                    {...textStyle}
                >
                    {label}
                </Typography>
            )}
            <input
                type="number"
                name={name}
                onChange={({ target: { value } }) => {
                    if (value === '') {
                        setValue('');
                        return;
                    }
                    const cleanedValue = value.replace(/[^\d.]/g, '');

                    const decimalIndex = cleanedValue.indexOf('.');
                    if (decimalIndex !== -1) {
                        const decimalPortion = cleanedValue.slice(decimalIndex + 1);
                        if (decimalPortion.length > 2) {
                            return;
                        }
                    }
                    setValue(Number(cleanedValue));
                }}
                value={currentValue}
                style={{
                    border: `${borderColor} solid 1px`,
                    padding: '9px 14px',
                    borderRadius: '4px',
                    width: '100px',
                    backgroundColor: disabled ? '#FADABB40' : 'white',
                }}
                disabled={disabled}
            />
        </Flex>
    );
};

RateInputField.defaultProps = {
    flexDirection: 'row',
};
